// 更新日志 — 接 standard-service /common/log/bulletin/page (av=1)
// 反编译参考：com.ulucu.view.activity.UpdateLogActivity
// 路由：/m/changelog
import { Empty, Spin, Tag, Typography, message as antdMessage } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildChangelogDetailUrl, fetchChangelogPage, type LogBulletin } from '../api/changelog.js';
import { useAuthStore } from '../stores/auth.js';
import { colors } from '../theme/colors.js';
import MobileHeader from '../components/MobileHeader.js';

const PAGE_SIZE = 20;
const PRIMARY_TEXT = colors.primary.text;

export default function Changelog() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const token = useAuthStore((s) => s.token);

	const [items, setItems] = useState<LogBulletin[] | null>(null);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = (p: number, replace = false) => {
		setLoading(true);
		setError(null);
		fetchChangelogPage({ page: p, count: PAGE_SIZE })
			.then((r) => {
				setItems((prev) => (replace || !prev ? r.items : [...prev, ...r.items]));
				const totalLoaded = (replace ? 0 : items?.length ?? 0) + r.items.length;
				setHasMore(totalLoaded < Number(r.total_count || '0'));
			})
			.catch((e) => setError((e as Error).message ?? 'load failed'))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		load(1, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openDetail = async (b: LogBulletin) => {
		if (!token) {
			antdMessage.info(t('common.loading'));
			return;
		}
		// 异步：要先从后端 /api/internal/region 拿当前部署 region 对应的 microservice 域名
		const url = await buildChangelogDetailUrl(b.bulletin_id, token);
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const onLoadMore = () => {
		if (loading || !hasMore) return;
		const next = page + 1;
		setPage(next);
		load(next);
	};

	return (
		<div style={{ background: colors.bg.page, minHeight: '100vh' }}>
		<div style={{ maxWidth: 720, margin: '0 auto' }}>
			<MobileHeader title={t('mtab.profile.changelog')} onBack={() => navigate(-1)} />

			<div style={{ padding: 12 }}>
				{!items && loading && (
					<div style={{ textAlign: 'center', padding: 60 }}>
						<Spin />
					</div>
				)}

				{error && !items && (
					<div role="alert" style={{ padding: 24, textAlign: 'center', color: colors.status.dangerText }}>{error}</div>
				)}

				{items && items.length === 0 && (
					<div style={{ padding: '60px 0' }}>
						<Empty description={t('mtab.profile.changelog_empty')} />
					</div>
				)}

				{items && items.length > 0 && (
					<>
						{items.map((b) => (
							<button
								key={b.bulletin_id}
								type="button"
								className="a11y-button"
								onClick={() => openDetail(b)}
								aria-label={`#${b.bulletin_id} ${b.is_unread === '1' ? t('changelog.unread') : ''} ${b.publish_date || ''} ${b.title}`}
								style={{
									marginBottom: 12,
									borderRadius: 12,
									cursor: 'pointer',
									background: colors.bg.card,
									padding: 16,
									border: `1px solid ${colors.graphic.divider}`,
									textAlign: 'start',
								}}
							>
								<div
									style={{
										display: 'flex',
										alignItems: 'baseline',
										gap: 10,
										marginBottom: 10,
										flexWrap: 'wrap',
									}}
								>
									<Tag color="orange" style={{ fontSize: 12, padding: '0 8px', margin: 0 }}>
										#{b.bulletin_id}
									</Tag>
									{b.is_unread === '1' && (
										<Tag color="red" style={{ fontSize: 11, padding: '0 6px', margin: 0 }}>
											{t('changelog.unread')}
										</Tag>
									)}
									<Typography.Text style={{ fontSize: 12, color: colors.text.secondary }}>
										{b.publish_date || (b.create_time ?? '').slice(0, 10) || '—'}
									</Typography.Text>
								</div>
								<Typography.Text style={{ fontSize: 14, color: colors.text.primary, lineHeight: 1.6 }}>
									{b.title}
								</Typography.Text>
							</button>
						))}

						{hasMore && (
							<button
								type="button"
								className="a11y-button"
								onClick={onLoadMore}
								disabled={loading}
								style={{
									textAlign: 'center',
									padding: 16,
									minHeight: 44,
									color: loading ? colors.text.disabled : PRIMARY_TEXT,
									fontSize: 14,
									fontWeight: 500,
									cursor: loading ? 'default' : 'pointer',
								}}
							>
								{loading ? <Spin size="small" /> : t('changelog.load_more')}
							</button>
						)}
						{!hasMore && (
							<div
								style={{
									textAlign: 'center',
									padding: 16,
									color: colors.text.tertiary,
									fontSize: 12,
								}}
							>
								{t('changelog.no_more')}
							</div>
						)}
					</>
				)}
			</div>
		</div>
		</div>
	);
}
